import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import CookieConsent from 'react-cookie-consent';
import { initializeGA, usePageTracking } from './hooks/useAnalytics';
import { useAuthStore } from './stores/authStore';
import { useSubscriptionStore } from './stores/subscriptionStore';
import ScrollToTop from './components/common/ScrollToTop';
import LoadingScreen from './components/common/LoadingScreen';
import Dashboard from './components/dashboard/Dashboard';
import { ApiErrorBoundary } from './components/common/ApiErrorBoundary';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import Hero from './components/Hero';
import FutureJobs from './components/FutureJobs';
import BlogSection from './components/blog/BlogSection';
import HowWeWork from './components/HowWeWork';
import Documentation from './pages/Documentation';
import PricingPlans from './pages/PricingPlans';
import CookiesPolicy from './pages/CookiesPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import MeshGradient from './components/MeshGradient';
import UserProfile from './components/profile/UserProfile';
import SubscriptionSuccess from './pages/dashboard/SubscriptionSuccess';
import SubscriptionCancel from './pages/dashboard/SubscriptionCancel';
import AuthCallback from './components/auth/AuthCallback';
import { fetchFromSupabase } from './config/supabase';
import toast from 'react-hot-toast';
import { geminiService } from './services/geminiService';

const About = React.lazy(() => import('./pages/About'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const BlogList = React.lazy(() => import('./components/blog/BlogList'));
const BlogPost = React.lazy(() => import('./components/blog/BlogPost'));

const MyCoursesPage = React.lazy(() => import('./pages/courses/MyCoursesPage'));
const PurchasedCourseDetailPage = React.lazy(() => import('./pages/courses/PurchasedCourseDetailPage'));
const CourseLibraryPage = React.lazy(() => import('./pages/courses/CourseLibraryPage'));
const CourseDetailPage = React.lazy(() => import('./pages/courses/CourseDetailPage'));

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, initialized } = useAuthStore();

  if (!initialized) {
    return <LoadingScreen />;
  }

  if (!user) {
    toast.error("Musisz być zalogowany, aby uzyskać dostęp do tej strony.");
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

import { DebugErrorBoundary } from './components/DebugErrorBoundary';

function HomePage() {
  return (
    <>
      <DebugErrorBoundary name="Hero">
        <Hero />
      </DebugErrorBoundary>
      <DebugErrorBoundary name="HowWeWork">
        <HowWeWork />
      </DebugErrorBoundary>
      <DebugErrorBoundary name="BlogSection">
        <BlogSection />
      </DebugErrorBoundary>
      <DebugErrorBoundary name="FutureJobs">
        <FutureJobs />
      </DebugErrorBoundary>
    </>
  );
}

function App() {
  const { user } = useAuthStore();
  const { fetchSubscriptionStatus, setPlan } = useSubscriptionStore();
  const [isAppLoading, setIsAppLoading] = React.useState(true);
  const [consentGiven, setConsentGiven] = useState(
    () => localStorage.getItem('cookieConsent') === 'true'
  );

  useEffect(() => {
    if (consentGiven) {
      initializeGA();
    }
  }, [consentGiven]);

  usePageTracking(consentGiven);

  const handleAcceptCookie = () => {
    localStorage.setItem('cookieConsent', 'true');
    setConsentGiven(true);
    initializeGA();
  };
  


  React.useEffect(() => {
    const initApp = async () => {
      if (!user) {
        setIsAppLoading(false);
        return;
      }
      
      try {
        const cachedPlan = localStorage.getItem('subscription_plan');
        if (cachedPlan) {
          setPlan(cachedPlan as 'free' | 'premium');
        }
        
        try {
          const { data: subscription, error } = await fetchFromSupabase<any>({
            tableName: 'subscriptions',
            columns: '*',
            filters: { 
              user_id: user.id,
            },
          });
          
          const activeSubscription = Array.isArray(subscription) 
              ? subscription.find(sub => sub.status === 'active')
              : (subscription?.status === 'active' ? subscription : null);
              
          if (activeSubscription) {
            const plan = activeSubscription.plan_id === 'price_premium' ? 'premium' : 'free';
            setPlan(plan);
            localStorage.setItem('subscription_plan', plan);
            console.log(`[App Init] Ustawiono plan na '${plan}' na podstawie aktywnej subskrypcji z DB.`);
          } else {
            const currentState = useSubscriptionStore.getState();
            if (currentState.plan !== 'free') {
                console.log('[App Init] Brak aktywnej subskrypcji w DB, ustawiam plan na \'free\'.');
                setPlan('free');
                localStorage.setItem('subscription_plan', 'free');
            }
          }
        } catch (err) {
          console.warn('[App Init] Błąd podczas pobierania subskrypcji bezpośrednio:', err);
        }
        
        await fetchSubscriptionStatus(user.id);
      } catch (error) {
        console.error('[App Init] Błąd podczas inicjalizacji aplikacji:', error);
        toast.error('Wystąpił problem podczas ładowania danych');
      } finally {
        setIsAppLoading(false);
      }
    };
    
    initApp();
  }, [user, fetchSubscriptionStatus, setPlan]);

  if (isAppLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col relative">
      <ApiErrorBoundary>
        <ScrollToTop />
        <DebugErrorBoundary name="MeshGradient">
          <MeshGradient />
        </DebugErrorBoundary>
        <DebugErrorBoundary name="Navigation">
          <Navigation />
        </DebugErrorBoundary>
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/o-nas" element={<React.Suspense fallback={<LoadingScreen />}><About /></React.Suspense>} />
            <Route path="/faq" element={<React.Suspense fallback={<LoadingScreen />}><FAQ /></React.Suspense>} />
            <Route path="/blog" element={<React.Suspense fallback={<LoadingScreen />}><BlogList /></React.Suspense>} />
            <Route path="/blog/:slug" element={<React.Suspense fallback={<LoadingScreen />}><BlogPost /></React.Suspense>} />
            <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
            <Route path="/regulamin" element={<Terms />} />
            <Route path="/dokumentacja" element={<Documentation />} />
            <Route path="/cookies" element={<CookiesPolicy />} />
            <Route path="/plany" element={<PricingPlans />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />} />

            <Route path="/profil" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><React.Suspense fallback={<LoadingScreen />}><Dashboard /></React.Suspense></ProtectedRoute>} />
            <Route path="/dashboard/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
            <Route path="/dashboard/cancel" element={<ProtectedRoute><SubscriptionCancel /></ProtectedRoute>} />

            <Route path="/moje-kursy" element={
              <ProtectedRoute>
                <React.Suspense fallback={<LoadingScreen />}>
                  <MyCoursesPage />
                </React.Suspense>
              </ProtectedRoute>
            } />
            <Route path="/moje-kursy/:courseId" element={
              <ProtectedRoute>
                <React.Suspense fallback={<LoadingScreen />}>
                  <PurchasedCourseDetailPage />
                </React.Suspense>
              </ProtectedRoute>
            } />
            <Route path="/kursy/:courseId" element={
              <React.Suspense fallback={<LoadingScreen />}>
                <CourseDetailPage />
              </React.Suspense>
            } />
            <Route path="/biblioteka-kursow" element={
              <React.Suspense fallback={<LoadingScreen />}>
                <CourseLibraryPage />
              </React.Suspense>
            } />
          </Routes>
        </main>
        <DebugErrorBoundary name="Footer">
          <Footer />
        </DebugErrorBoundary>
        <CookieConsent
          location="bottom"
          buttonText="Akceptuję"
          cookieName="userCookieConsent"
          style={{ background: "#2B373B" }}
          buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
          expires={150}
          onAccept={handleAcceptCookie}
        >
          Ta strona korzysta z plików cookie, aby zapewnić najlepszą jakość korzystania z naszej witryny.{" "}
          <Link to="/polityka-prywatnosci" style={{ color: "#a1a1aa" }}>
            Dowiedz się więcej
          </Link>
        </CookieConsent>
      </ApiErrorBoundary>
    </div>
  );
}

export default App;
